// src/utils/assetUploadForm.js
import { db } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
  serverTimestamp,
  limit,
  orderBy,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";

/**
 * Ensure there is exactly one asset-upload form record for this org.
 * Returns the formId (existing or newly created).
 *
 * NOTE: No assumptions about FormContext or generic form permissions here.
 * We only enforce DAM-form intent: isAssetUploadForm + organizationId.
 */
export async function ensureAssetUploadFormForOrg({ organizationId, userId }) {
  if (!organizationId || !userId) {
    throw new Error("Missing organizationId or userId");
  }

  try {
    // 1) Prefer an existing DRAFT AUF (safe to reuse while still in setup)
    const draftQ = query(
      collection(db, "forms"),
      where("organizationId", "==", organizationId),
      where("isAssetUploadForm", "==", true),
      where("status", "==", "draft"),
      orderBy("lastModified", "desc"),   // most recent draft first
      limit(1)
    );
    const draftSnap = await getDocs(draftQ);
    if (!draftSnap.empty) {
      const draftId = draftSnap.docs[0].id;
      console.log("[ensureAssetUploadFormForOrg] Reusing existing DRAFT AUF:", draftId);
      return draftId;
    }

    // 2) DO NOT reuse archived or published here — user clicked "Create"
    //    Intentionally no lookup/return for status === 'archived' or 'published'

    // 3) Create a new special form (minimal viable doc; builder will fill the rest)
    const formId = uuidv4();
    await setDoc(doc(db, "forms", formId), {
      owner: userId,
      createdBy: userId,
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp(),
      lastModifiedBy: userId,

      organizationId,
      isAssetUploadForm: true,
      status: "draft",

      // basic metadata so your builder navbar has something to show
      title: "Asset Upload Form",
      description:
        "Define required metadata for assets uploaded by your organization.",
      isPublic: false,
      orgAccess: "none",
      sharedWith: {},
    });

    console.log("[ensureAssetUploadFormForOrg] Created new AUF draft:", formId);
    return formId;
  } catch (e) {
    console.error("[ensureAssetUploadFormForOrg] Failed:", e);
    throw e;
  }
}

